import { useEffect, useMemo } from "react";
import Scrollable from "../../Shared/Edit/Scrollable";
import AttributeTableGrid from "./Grid/AttributeTableGrid";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import { useAttributes, useChangeBooleans, useFetchedValues } from "../../../Hooks/Display/useComponentProperties";
import { getAllAttributes } from "../../Helpers/Schema/Schema";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import { getEntityPages, getReferenceAttributeEntityPages } from "../../../API/Display/Display";
import { useSelectedPage } from "../../../Hooks/Display/usePages";
import { filterAttributesForComponent } from "./utils";
import { DragAndDrop } from "@progress/kendo-react-common";
import Skeleton from "../../Shared/Skeleton/Skeleton";
import ComponentAttributeToolbar from "./ComponentAttributeToolbar";
import SelectAttributesDialog from "./SelectAttributesDialog/SelectAttributesDialog";
import SyncAttributeDialog from "./SyncAttributeDialog/SyncAttributeDialog";
import { bySystemAttributeDesc } from "../utils";
import { useTranslation } from "react-i18next";
import { getAttributes } from "../../../API/Attribute/Attribute";

function AttributeTable() {

    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const entityId = usePropsStoreValue("entityId");

    const [state, dispatch] = useAttributes();
    const [fetchedValues, { updateAttributeTableFetchedValue }] = useFetchedValues();
    const [, setChangeBoolean] = useChangeBooleans();

    const { t } = useTranslation("display/tables/attribute", { useSuspense: false });

    const selectableAttributes = useMemo(() => {
        if (fetchedValues && fetchedValues.attributeTable && fetchedValues.attributeTable.attributes) {
            const attributes = fetchedValues.attributeTable.attributes.sort(bySystemAttributeDesc);
            if (selectedComponent.componentType === "Calendar") {
                return attributes.filter(attribute => attribute.attributeType !== "Image");
            }
            return attributes.sort((a,b) => a.attributeName.localeCompare(b.attributeName)).sort(bySystemAttributeDesc);
        }
        return [];
    }, [fetchedValues])

    useEffect(() => {
        getData();
    }, [selectedComponent])

    useEffect(() => {
        if (selectedComponent.attributes.length !== state.attributes.length) {
            setChangeBoolean("isAttributeTableChanged", true);
        } else if (selectedComponent.attributes.length === state.attributes.length) {
            const isChanged = selectedComponent.attributes.some(attribute => {
                const selectedAttribute = state.attributes.find(attr => attr.attributeId === attribute.attributeId);
                const returnValue = selectedAttribute
                    ? (
                        selectedAttribute.hyperlink !== attribute.hyperlink ||
                        selectedAttribute.cellRules !== attribute.cellRules ||
                        selectedAttribute.targetPageId !== (attribute.targetPageId || "") ||
                        selectedAttribute.tooltip !== attribute.tooltip ||
                        (
                            (selectedComponent.componentType === "Form" || selectedComponent.componentType === "View")
                                ? selectedAttribute.group !== attribute.group
                                : false
                        )
                    )
                    : true; // new attribute added and previous attribute removed
                return returnValue;
            });
            const isReordered = selectedComponent.attributes.some((attribute, index) => {
                const selectedAttribute = state.attributes[index];
                return selectedAttribute.attributeId !== attribute.attributeId;
            });
            setChangeBoolean("isAttributeTableChanged", isChanged || isReordered);
        }
    }, [state.attributes, selectedComponent.attributes])

    async function getData() {
        if (state.loading) {
            const attributes = await getAttributes(entityId);
            const filteredAttributes = filterAttributesForComponent(attributes, selectedComponent.componentType, selectedPage.subPageType);
            const referenceAttributePages = await getReferenceAttributePages(filteredAttributes);
            updateAttributeTableFetchedValue("attributes", filteredAttributes);
            updateAttributeTableFetchedValue("referenceAttributePages", referenceAttributePages);
            const attributesTableData = selectedComponent.attributes.map(attribute => {
                const selectedAttribute = attributes.find(attr => attr._id === attribute.attributeId);
                return {
                    ...attribute,
                    attributeName: selectedAttribute.attributeName,
                    attributeType: selectedAttribute.attributeType,
                    attributeLabel: selectedAttribute.attributeLabel,
                    entityId: selectedAttribute.entityId,
                }
            })
            dispatch.setAttributes(attributesTableData)
        }
    }

    async function getReferenceAttributePages(attributes) {
        const allPages = [];
        for (const attribute of attributes) {
            attribute.entityId = entityId;
            if (attribute.attributeType === "Entity Lookup") {
                const pages = await getEntityPages(attribute.references.entityId, selectedPage.portalId);
                const singleAndEditRecordPages = pages.data.filter(page => page.subPageType === "Single Record" || page.subPageType === "Edit Record");
                allPages.push(...singleAndEditRecordPages);
                if (pages.length > 0) {
                    attribute.entityId = pages.references.entityId;
                }
            }
        }
        return allPages;
    }

    function toggleSelectAttributeDialog() {
        dispatch.toggleSelectAttributeDialog();
    }

    function setSelectedAttributes(selectedAttributes) {
        const updatedSelectedAttributes = selectedAttributes.map(selectedAttribute => {
            const matchingAttribute = selectableAttributes.find(attr => attr._id === selectedAttribute.attributeId);
            const previousSelectedAttribute = state.attributes.find(attr => attr.attributeId === selectedAttribute.attributeId);
            const updatedAttribute = {
                ...selectedAttribute,
                attributeName: matchingAttribute.attributeName,
                attributeType: matchingAttribute.attributeType,
                attributeLabel: matchingAttribute.attributeLabel,
                entityId: matchingAttribute.entityId,
                group: matchingAttribute.group,
                cellRules: previousSelectedAttribute ? previousSelectedAttribute.cellRules : selectedAttribute.cellRules,
                hyperlink: previousSelectedAttribute ? previousSelectedAttribute.hyperlink : selectedAttribute.hyperlink,
                targetPageId: previousSelectedAttribute ? previousSelectedAttribute.targetPageId : selectedAttribute.targetPageId,
                tooltip: previousSelectedAttribute ? previousSelectedAttribute.tooltip : selectedAttribute.tooltip,
            }

            return updatedAttribute;
        });
        dispatch.setAttributes(updatedSelectedAttributes);
    }

    function handleSelect(selectedAttributes) {
        setSelectedAttributes(selectedAttributes);
        toggleSelectAttributeDialog();
    }

    if (state.loading) {
        return (
            <Skeleton
                rows={[
                    { columns: [{ size: 12 }] },
                    { full: true }
                ]} />
        )
    }

    return (
        <Scrollable>
            {state.showSelectAttributeDialog && (
                <SelectAttributesDialog
                    attributes={selectableAttributes}
                    componentAttributes={state.attributes}
                    onSelect={handleSelect}
                    onCancel={toggleSelectAttributeDialog}
                />
            )}
            {state.showSyncAttributeDialog && <SyncAttributeDialog componentTypes={["Table", "Cards"]} />}
            <ComponentAttributeToolbar
                onRecommend={setSelectedAttributes}
                showSelectAttributeButton={true}
                showOptimizeAIButton={false}
            />
            <div className="p-2">
                <DragAndDrop>
                    <AttributeTableGrid />
                </DragAndDrop>
            </div>
        </Scrollable>
    )
}

export default AttributeTable;